import {Language} from "../interfaces/Language";
import {Region} from "../interfaces/Region";
import {get as getCookie} from "es-cookie";
import {RoomType} from "../interfaces/RoomType";

export class RegionHelper {
	/**
	 * @internal
	 */
	private static roomData = {
		en: {
			bathroom: {
				title: 'Bathroom',
				pathFragment: 'bathroom',
				basePath: '/bathroom',
				productType: 'bath',
				roomName: 'bath'
			},
			bath: {
				title: 'Bathroom',
				pathFragment: 'bath',
				basePath: '/bathroom',
				productType: 'bath',
				roomName: 'bath'
			},
			kitchen: {
				title: 'Kitchen',
				pathFragment: 'kitchen',
				basePath: '/kitchen',
				productType: 'kitchen',
				roomName: 'kitchen'
			},
			parts: {
				title: 'Replacement Parts',
				pathFragment: 'parts',
				basePath: '/parts-support/replacement-parts',
				productType: 'parts',
				roomName: 'parts'
			}
		},
		es: {
			bathroom: {
				title: 'Baño',
				pathFragment: 'bano',
				basePath: '/bano',
				productType: 'bath',
				roomName: 'baño'
			},
			bath: {
				title: 'Baño',
				pathFragment: 'bano',
				basePath: '/bano',
				productType: 'bath',
				roomName: 'baño'
			},
			kitchen: {
				title: 'Cocina',
				pathFragment: 'cocina',
				basePath: '/cocina',
				productType: 'kitchen',
				roomName: 'cocina'
			},
			parts: {
				title: 'Partes de Repuesto',
				pathFragment: 'partes',
                basePath: '/partes-ayuda/repuestos',
				productType: 'parts',
				roomName: 'partes'
			}
		},
		fr: {
			bathroom: {
				title: 'Salle de Bains',
				pathFragment: 'salle-de-bains',
				basePath: '/salle-de-bains',
				productType: 'bath',
				roomName: 'salle de bains'
			},
			bath: {
				title: 'Salle de Bains',
				pathFragment: 'salle-de-bains',
				basePath: '/salle-de-bains',
				productType: 'bath',
				roomName: 'salle de bains'
			},
			kitchen: {
				title: 'Cuisine',
				pathFragment: 'cuisine',
				basePath: '/cuisine',
				productType: 'kitchen',
				roomName: 'cuisine'
			},
			parts: {
				title: 'Pièces de Rechange',
				pathFragment: 'pieces',
				basePath: '/pieces-et-support/pieces-de-rechange',
				productType: 'parts',
				roomName: 'pieces'
			}
		}
	}

	/**
	 * Gets the current site language as defined in the 'language' cookie
	 * @returns {string}
	 */
	static detectLanguage() {
		return getCookie('language') || Language.EN;
	}

	/**
	 * Gets the current site region as defined in the 'region' cookie
	 * @param {string} host
	 * @returns {string}
	 */
	static detectRegion(host?: string) {
		return getCookie('region') ? getCookie('region').toUpperCase() : Region.US;
	}

	/**
	 * Returns whether the current language is the default language for the given region
	 * @returns {boolean}
	 */
	static isDefaultLanguage(): boolean {
		let language = this.detectLanguage();
		let region = this.detectRegion();
		/*if (region === Region.US || region === Region.CA) {
			return language === Language.EN;
		} else if (region === Region.LATAM) {
			return language === Language.ES;
		}*/
		// 10/24/2018 EN is now default for all sites
		return language === Language.EN;

		/*return false;*/
	}

	/**
	 * Returns the localized version of a given URL
	 * @param {string} url
	 * @returns {string}
	 */
	static localizedUrl(url: string): string {
		let language = this.detectLanguage();
		return (RegionHelper.isDefaultLanguage()) ? url : `/${language}${url}`;
	}

	static detectRoomPath(path: string) {
		let language = this.detectLanguage();

		if (path.indexOf(this.roomData[language].bathroom.productType) >= 0 || path.indexOf(this.roomData[language].bath.productType) >= 0) {
			return this.roomData[language].bathroom.roomName;
		} else if (path.indexOf(this.roomData[language].kitchen.productType) >= 0) {
			return this.roomData[language].kitchen.roomName;
		} else if (path.indexOf(this.roomData[language].parts.productType) >= 0) {
			return this.roomData[language].parts.roomName;
		} else {
			return null;
		}
	}

	/**
	 * Detects the currently-selected room from a given URL path
	 * @param {string} path
	 * @returns {any}
	 */
	static detectRoom(path: string) {
		let language = this.detectLanguage();

		if (path.indexOf(this.roomData[language].bathroom.pathFragment) >= 0 || path.indexOf(this.roomData[language].bath.pathFragment) >= 0) {
			return this.roomData[language].bathroom.roomName;
		} else if (path.indexOf(this.roomData[language].kitchen.pathFragment) >= 0) {
			return this.roomData[language].kitchen.roomName;
		} else if (path.indexOf(this.roomData[language].parts.pathFragment) >= 0) {
			return this.roomData[language].parts.roomName;
		} else {
			return null;
		}
	}

	static detectStaticRoom(englishName) {
		let language = this.detectLanguage();

		if (englishName === RoomType.KITCHEN) {
			return this.roomData[language].kitchen.roomName;
		}

		if (englishName === RoomType.BATHROOM) {
			return this.roomData[language].bathroom.roomName;
		}
	}

	/**
	 * Returns the base room URL fragment (e.g. /bath or /fr/salles-de-bain) from a given product type
	 * @param {string} path
	 * @returns {any}
	 */
	static roomBaseUrlByProductType(path: string) {
		let basePath: string;
		let language = this.detectLanguage();

		if (path.indexOf(this.roomData[language].bathroom.productType) >= 0 || path.indexOf(this.roomData[language].bath.productType) >= 0) {
			basePath = this.roomData[language].bathroom.basePath;
		} else if (path.indexOf(this.roomData[language].kitchen.productType) >= 0) {
			basePath = this.roomData[language].kitchen.basePath;
		} else if (path.indexOf(this.roomData[language].parts.productType) >= 0) {
			basePath = this.roomData[language].parts.basePath;
		} else {
			return null;
		}

		if (!this.isDefaultLanguage()) {
			return `/${language}${basePath}`;
		} else {
			return basePath;
		}
	}

	/**
	 * Returns the base room URL fragment (e.g. /bath or /fr/salles-de-bain) from a given URL path
	 * @param {string} path
	 * @returns {any}
	 */
	static roomBaseUrl(path: string) {
		let basePath: string;
		let language = this.detectLanguage();

		if (path.indexOf(this.roomData[language].bathroom.pathFragment) >= 0 || path.indexOf(this.roomData[language].bath.pathFragment) >= 0) {
			basePath = this.roomData[language].bathroom.basePath;
		} else if (path.indexOf(this.roomData[language].kitchen.pathFragment) >= 0) {
			basePath = this.roomData[language].kitchen.basePath;
		} else if (path.indexOf(this.roomData[language].parts.pathFragment) >= 0) {
			basePath = this.roomData[language].parts.basePath;
		} else {
			return null;
		}

		if (!this.isDefaultLanguage()) {
			return `/${language}${basePath}`;
		} else {
			return basePath;
		}
	}

	static removeBaseUrl(path) {
		let baseUrl = this.roomBaseUrl(path);
		return path.replace(baseUrl, '');
	}
}



// WEBPACK FOOTER //
// ./js/PFF/angular-modules/common/helpers/region.helper.ts